/* Modulo: Review Signature — endless peek rail
   ---------------------------------------------------------------------------
   Native scroll-snap does the scrolling (so the rail swipes with momentum
   before this file has even parsed, and there is no CLS on hydrate). This adds
   three things on top:

     1. Endless looping, by cloning whole sets of cards either side of the real
        ones and teleporting the scroll position by an exact multiple of one
        set width whenever we near a physical end. Because the content repeats
        every setW, the teleport is invisible.
     2. Centre detection, which drives the `.is-active` card and the dots.
     3. Optional autoplay, paused on hover, focus, and hidden tabs.

   Without this file the rail is a plain, bounded, swipeable scroller.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var SEL = '[data-modulo-tsg]';
  var CARD = '.modulo-tsg__card';
  var reduceMQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reduced() {
    // Queried live, never cached at load: a mid-session toggle of the OS
    // setting has to take effect without a reload.
    return !!(reduceMQ && reduceMQ.matches);
  }

  function init(root) {
    if (!root || root.__moduloTsg) return;

    var rail = root.querySelector('[data-modulo-tsg-rail]');
    if (!rail) return;

    var real = Array.prototype.slice.call(rail.querySelectorAll(CARD));
    var n = real.length;
    if (n < 2) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-modulo-tsg-dot]'));
    var prevBtn = root.querySelector('[data-modulo-tsg-prev]');
    var nextBtn = root.querySelector('[data-modulo-tsg-next]');
    var wantLoop = root.hasAttribute('data-tsg-loop');
    var delay = parseFloat(root.getAttribute('data-tsg-autoplay') || '0') * 1000;

    var loop = false;
    var setW = 0;
    var copies = 0;
    var index = 0;
    var timer = null;
    var raf = 0;
    var resizeT = null;
    var paused = false;
    var dead = false;
    var pending = null;
    var lastW = 0;

    /* ─── geometry ───
       Everything below works in ONE coordinate: `viewLeft`, the physical
       distance from the rail's left content edge to the left edge of the
       visible window, always in [0, maxScroll] whichever way the document
       runs. `offsetLeft` is physical too, so the two compose safely.

       Raw `scrollLeft` is not usable directly: in a spec-compliant RTL engine
       it starts at 0 and runs NEGATIVE toward the end, which would make the
       "near the start edge" teleport test true at rest and fire the loop on
       every frame. Legacy WebKit RTL instead runs max..0, which happens to
       match the physical reading already — so only the negative model needs
       converting, and it is detected by probing rather than sniffed. */

    var rtlNeg = false;
    function detectScrollModel() {
      rtlNeg = false;
      var dir = '';
      try { dir = window.getComputedStyle(rail).direction; } catch (e) { dir = ''; }
      if (dir !== 'rtl') return;
      var save = rail.scrollLeft;
      rail.scrollLeft = -1;
      rtlNeg = rail.scrollLeft < 0;
      rail.scrollLeft = save;
    }

    function maxScroll() { return rail.scrollWidth - rail.clientWidth; }
    function viewLeft() { return rtlNeg ? maxScroll() + rail.scrollLeft : rail.scrollLeft; }
    function rawFor(vl) { return rtlNeg ? vl - maxScroll() : vl; }

    // `offsetLeft` is measured from the offsetParent's padding edge. In an RTL
    // scroller the overflow extends LEFT of that edge, so card coordinates run
    // negative — while viewLeft() is measured from the leftmost content edge.
    // The two frames differ by exactly maxScroll, and mixing them makes the
    // nearest-centre search lock onto an off-screen clone forever: no visible
    // card ever gets .is-active, the grow mechanic never fires, and every glide
    // target lands past the end of the rail.
    function centerOf(el) {
      return el.offsetLeft + el.offsetWidth / 2 + (rtlNeg ? maxScroll() : 0);
    }
    function railMid() { return viewLeft() + rail.clientWidth / 2; }
    function cards() { return Array.prototype.slice.call(rail.querySelectorAll(CARD)); }

    function jump(vl) {
      // Snapping has to be suspended across the teleport, or the browser
      // animates back toward the snap point it was mid-flight to.
      var sb = rail.style.scrollBehavior;
      var ss = rail.style.scrollSnapType;
      rail.style.scrollBehavior = 'auto';
      rail.style.scrollSnapType = 'none';
      rail.scrollLeft = rawFor(vl);
      void rail.offsetWidth;
      rail.style.scrollSnapType = ss;
      rail.style.scrollBehavior = sb;
    }

    function glide(el) {
      var vl = centerOf(el) - rail.clientWidth / 2;
      if (reduced() || !rail.scrollTo) { rail.scrollLeft = rawFor(vl); return; }
      rail.scrollTo({ left: rawFor(vl), behavior: 'smooth' });
    }

    /* ─── clone train ─── */

    function makeClone(el) {
      var c = el.cloneNode(true);
      c.setAttribute('data-tsg-clone', '');
      c.setAttribute('aria-hidden', 'true');
      c.removeAttribute('id');
      // A duplicated editor id makes the theme editor select the wrong card.
      c.removeAttribute('data-shopify-editor-block');
      var focusable = c.querySelectorAll('a,button,input,select,textarea,[tabindex]');
      for (var i = 0; i < focusable.length; i++) focusable[i].setAttribute('tabindex', '-1');
      return c;
    }

    function clearClones() {
      var old = rail.querySelectorAll('[data-tsg-clone]');
      for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
    }

    function buildLoop() {
      clearClones();
      rail.classList.remove('is-loop');
      loop = false;
      setW = 0;
      copies = 0;

      if (!wantLoop || n < 3) return;

      // Cards are uniform (flex-basis with no grow or shrink), so one card plus
      // one gap is just the distance between two adjacent card origins. Taken
      // as a magnitude because an RTL flex row lays the second card out to the
      // LEFT of the first, which would otherwise read as a negative width and
      // silently switch looping off for every RTL storefront.
      // Drop the half-card spacers FIRST, then measure. They are what makes a
      // bounded rail centre its end cards, but inside a clone train they would
      // break the "content repeats every setW" invariant the teleport rests on —
      // and measuring before removing them would bake a stale pitch into setW.
      rail.classList.add('is-loop');

      var pitch = Math.abs(real[1].offsetLeft - real[0].offsetLeft);
      var vw = rail.clientWidth;
      if (!(pitch > 0) || !(vw > 0)) { rail.classList.remove('is-loop'); return; }

      var width = pitch * n;
      // One extra set beyond what the viewport needs guarantees the teleport
      // always lands with a full viewport of content still ahead of it.
      var reps = Math.ceil(vw / width) + 1;

      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();
      for (var c = 0; c < reps; c++) {
        for (var i = 0; i < n; i++) {
          before.appendChild(makeClone(real[i]));
          after.appendChild(makeClone(real[i]));
        }
      }
      rail.insertBefore(before, real[0]);
      rail.appendChild(after);

      loop = true;
      setW = width;
      copies = reps;
    }

    function normalize() {
      if (!loop || !(setW > 0)) return;
      var x = viewLeft();
      var max = maxScroll();
      // Jumping by an exact multiple of one set width lands on identical
      // content, so the teleport is invisible. copies = ceil(vw/setW) + 1
      // guarantees a full viewport still remains on the side just vacated.
      if (x < setW * 0.5) jump(x + setW * copies);
      else if (x > max - setW * 0.5) jump(x - setW * copies);
    }

    /* ─── active card + dots ─── */

    function sync() {
      raf = 0;
      if (dead) return;
      normalize();

      var list = cards();
      var mid = railMid();
      var best = null;
      var bestD = Infinity;
      for (var i = 0; i < list.length; i++) {
        var d = Math.abs(centerOf(list[i]) - mid);
        if (d < bestD) { bestD = d; best = list[i]; }
      }
      for (var j = 0; j < list.length; j++) {
        if (list[j] === best) list[j].classList.add('is-active');
        else list[j].classList.remove('is-active');
      }
      if (!best) return;
      // The requested card has arrived, so stop chaining off it.
      if (pending === best) pending = null;

      var idx = parseInt(best.getAttribute('data-tsg-i'), 10);
      if (isNaN(idx)) return;
      index = idx;
      for (var k = 0; k < dots.length; k++) {
        if (k === idx) {
          dots[k].classList.add('is-active');
          dots[k].setAttribute('aria-current', 'true');
        } else {
          dots[k].classList.remove('is-active');
          dots[k].removeAttribute('aria-current');
        }
      }
    }

    function queueSync() {
      if (raf) return;
      raf = window.requestAnimationFrame(sync);
    }

    /* ─── navigation ─── */

    function step(dir) {
      var list = cards();
      // Chain off the last REQUESTED card, not the last painted one. `.is-active`
      // only flips once the rail's centre crosses the midpoint of the travel, so
      // reading it during an in-flight smooth scroll recomputes the same target
      // and re-issues the same glide — two presses would advance one card, and
      // holding the arrow key would discard almost every repeat.
      var cur = pending ? list.indexOf(pending) : -1;
      if (cur < 0) {
        for (var i = 0; i < list.length; i++) {
          if (list[i].classList.contains('is-active')) { cur = i; break; }
        }
      }
      if (cur < 0) return;
      var t = cur + dir;
      if (t < 0 || t >= list.length) return; // normalize() keeps us clear of this
      pending = list[t];
      glide(list[t]);
    }

    function goToIndex(idx, instant) {
      // With clones there are several cards carrying this index — take the one
      // nearest the current position so the rail moves the short way round.
      var list = cards();
      var mid = railMid();
      var best = null;
      var bestD = Infinity;
      for (var i = 0; i < list.length; i++) {
        if (parseInt(list[i].getAttribute('data-tsg-i'), 10) !== idx) continue;
        var d = Math.abs(centerOf(list[i]) - mid);
        if (d < bestD) { bestD = d; best = list[i]; }
      }
      if (!best) return;
      if (instant) jump(centerOf(best) - rail.clientWidth / 2);
      else glide(best);
    }

    /* ─── autoplay ─── */

    function schedule() {
      if (dead || !(delay > 0)) return;
      timer = window.setTimeout(function () {
        timer = null;
        if (!dead && !paused && !document.hidden && !reduced()) step(1);
        schedule();
      }, delay);
    }
    function stopAuto() {
      if (timer) { window.clearTimeout(timer); timer = null; }
    }

    /* ─── wiring ─── */

    function onScroll() { queueSync(); }
    function onResize(force) {
      window.clearTimeout(resizeT);
      resizeT = window.setTimeout(function () {
        if (dead) return;
        // Width-gated. Mobile browsers fire `resize` every time the URL bar
        // collapses on vertical page scroll, and rebuilding then tears down and
        // re-inserts the whole clone train, clamps scrollLeft against the
        // briefly-shorter rail and re-centres — so the carousel visibly jumps
        // sideways while the shopper is only scrolling the page. Nothing in the
        // loop geometry is height-derived, so a height change cannot matter.
        var w = rail.clientWidth;
        if (force !== true && w === lastW) return;
        lastW = w;
        var keep = index;
        pending = null;
        buildLoop();
        if (loop) goToIndex(keep, true);
        sync();
      }, 150);
    }
    function pause() { paused = true; }
    function resume() { paused = false; }

    rail.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    root.addEventListener('pointerenter', pause);
    root.addEventListener('pointerleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', resume);

    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });
    for (var d = 0; d < dots.length; d++) {
      (function (el) {
        el.addEventListener('click', function () {
          var i = parseInt(el.getAttribute('data-modulo-tsg-dot'), 10);
          if (!isNaN(i)) goToIndex(i, false);
        });
      })(dots[d]);
    }

    detectScrollModel();
    buildLoop();
    lastW = rail.clientWidth;
    if (loop) jump(centerOf(real[0]) - rail.clientWidth / 2);
    sync();
    // One frame later, so the opening card does not animate itself open.
    window.requestAnimationFrame(function () { rail.classList.add('is-ready'); });
    schedule();

    // Late webfonts change card heights after a deferred script has measured.
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(function () { if (!dead) onResize(true); });
    }

    root.__moduloTsg = {
      selectBlock: function (el) {
        var idx = parseInt(el.getAttribute('data-tsg-i'), 10);
        if (!isNaN(idx)) goToIndex(idx, false);
      },
      destroy: function () {
        dead = true;
        stopAuto();
        window.clearTimeout(resizeT);
        if (raf) window.cancelAnimationFrame(raf);
        rail.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        root.__moduloTsg = null;
      }
    };
  }

  function boot(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    var els = host.querySelectorAll(SEL);
    for (var i = 0; i < els.length; i++) init(els[i]);
    if (host !== document && host.matches && host.matches(SEL)) init(host);
  }

  // An identical <script src> still executes once per tag, so a section added
  // twice in the theme editor would otherwise stack duplicate listeners.
  if (!window.__moduloTsgWired) {
    window.__moduloTsgWired = true;

    document.addEventListener('shopify:section:load', function (e) {
      boot(e.target);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var els = e.target.querySelectorAll ? e.target.querySelectorAll(SEL) : [];
      for (var i = 0; i < els.length; i++) {
        if (els[i].__moduloTsg) els[i].__moduloTsg.destroy();
      }
    });
    document.addEventListener('shopify:block:select', function (e) {
      var card = e.target;
      if (!card || !card.closest) return;
      var root = card.closest(SEL);
      if (root && root.__moduloTsg) root.__moduloTsg.selectBlock(card);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { boot(document); });
    } else {
      boot(document);
    }
  } else {
    boot(document);
  }
})();
